// Extracted from GuestDetail.jsx during the Feb 2026 component split.
// Contains EmailsTab (default export).
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CheckCircle2, Clock, Copy, History, MailCheck, MailX, Send } from "lucide-react";

// Guest-facing built-in email types shown as tabs. host_prep_reminder is host-only.
const EMAIL_TYPES = [
  { key: "booking_confirmation", label: "Booking confirmation" },
  { key: "intake_form", label: "Intake form" },
  { key: "form_reminder", label: "Intake reminder" },
  { key: "preshow_checkin", label: "Pre-show check-in" },
  { key: "day_of_reminder", label: "Day-of reminder" },
  { key: "post_thankyou", label: "Post-episode thank-you" },
];
const BUILT_IN_KEYS = EMAIL_TYPES.map(t => t.key);
const HOST_ONLY_KEYS = new Set(["host_prep_reminder"]);

// Resend lifecycle events → what the host sees in the Delivery column.
const DELIVERY_LOOK = {
  delivered: { label: "Delivered", cls: "pgm-badge-olive", icon: MailCheck },
  opened: { label: "Opened", cls: "pgm-badge-olive", icon: MailCheck },
  clicked: { label: "Clicked", cls: "pgm-badge-olive", icon: MailCheck },
  bounced: { label: "Bounced", cls: "pgm-badge-orange", icon: MailX },
  failed: { label: "Bounced", cls: "pgm-badge-orange", icon: MailX },
  complained: { label: "Marked spam", cls: "pgm-badge-orange", icon: MailX },
  suppressed: { label: "Suppressed", cls: "pgm-badge-orange", icon: MailX },
  canceled: { label: "Cancelled", cls: "pgm-badge-orange", icon: MailX },
  delivery_delayed: { label: "Delayed", cls: "pgm-badge-orange", icon: Clock },
  queued: { label: "Queued", cls: "pgm-badge-magenta", icon: Clock },
  scheduled: { label: "Queued", cls: "pgm-badge-magenta", icon: Clock },
  sent: { label: "Accepted", cls: "pgm-badge-magenta", icon: Clock },
};

function DeliveryChip({ log, fmtWhen }) {
  if (!log.sent) {
    return (
      <span className="pgm-badge pgm-badge-orange" title={log.reason || ""} data-testid={`delivery-${log.id}`}>
        <AlertTriangle size={11} /> Never sent
      </span>
    );
  }
  const status = log.delivery_status || "sent";
  const look = DELIVERY_LOOK[status] || DELIVERY_LOOK.sent;
  const Icon = look.icon;
  if (!log.provider_id) {
    return (
      <span className="pgm-badge" style={{ opacity: 0.6 }} title="This send predates delivery tracking, so Resend has no message id for us to follow up on." data-testid={`delivery-${log.id}`}>
        <Clock size={11} /> Not tracked
      </span>
    );
  }
  const timeline = (log.delivery_events || [])
    .map(e => `${(DELIVERY_LOOK[e.event]?.label) || e.event} — ${fmtWhen(e.at)}${e.reason ? ` (${e.reason})` : ""}`)
    .join("\n");
  const title = [
    timeline || "Accepted by Resend. Waiting on the delivery confirmation (checked every 10 minutes).",
    log.delivery_reason ? `Reason: ${log.delivery_reason}` : "",
  ].filter(Boolean).join("\n");
  return (
    <span className={`pgm-badge ${look.cls}`} title={title} data-testid={`delivery-${log.id}`}>
      <Icon size={11} /> {look.label}
    </span>
  );
}

function EmailsTab({ guestId, guest, emailStatus, show, onSent }) {
  const [active, setActive] = useState("booking_confirmation");
  const [data, setData] = useState({ subject: "", body: "", to: "" });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const templates = show?.email_templates || {};
  const dynamicTypes = [
    ...EMAIL_TYPES,
    ...Object.keys(templates)
      .filter(k => !BUILT_IN_KEYS.includes(k) && !HOST_ONLY_KEYS.has(k))
      .map(k => ({ key: k, label: (templates[k]?.label) || k })),
  ];

  const loadLog = useCallback(() => {
    api.get(`/guests/${guestId}/email-log`).then(r => setLog(r.data)).catch(() => {});
  }, [guestId]);

  useEffect(() => {
    api.get(`/guests/${guestId}/emails/${active}`).then(r => { setData(r.data); setStatus(null); });
  }, [active, guestId]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const copy = () => navigator.clipboard.writeText(`${data.subject}\n\n${data.body}`);

  const activeLabel = dynamicTypes.find(t => t.key === active)?.label || active;

  const send = async () => {
    if (!data.to) { setStatus({ kind: "warn", text: "Add a guest email on the Info tab first." }); return; }
    if (active === "preshow_checkin" && !((guest?.broadcast_link || "").trim())) {
      setStatus({ kind: "warn", text: "No studio link yet — add the guest's Broadcast / StreamYard link on the Info tab (tap 'Use show default' to copy the show-level link) before sending the pre-show email." });
      return;
    }
    setSending(true); setStatus(null);
    try {
      const html = `<pre style="font-family: Arial,sans-serif; white-space: pre-wrap;">${data.body.replace(/</g, "&lt;")}</pre>`;
      const { data: res } = await api.post("/email/send", {
        to: data.to, subject: data.subject, html,
        guest_id: guestId, template_key: active, template_label: activeLabel,
      });
      if (res.sent) setStatus({ kind: "ok", text: `Sent to ${data.to}` });
      else setStatus({ kind: "warn", text: res.reason || "Not sent" });
      loadLog();
    } catch (e) {
      setStatus({ kind: "warn", text: e?.response?.data?.detail || "Failed to send" });
    } finally { setSending(false); }
  };

  const lastSentFor = (key) => log.find(l => l.template_key === key && l.sent);
  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch (e) { return iso?.slice(0, 16) || ""; }
  };

  const needsBroadcastLink = active === "preshow_checkin"
    && !((guest?.broadcast_link || "").trim());

  return (
    <div className="pgm-reveal">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {dynamicTypes.map(e => (
          <button key={e.key} onClick={() => setActive(e.key)} className={`pgm-btn ${active === e.key ? "" : "pgm-btn-ghost"}`} style={{ padding: "8px 14px", fontSize: 11 }} data-testid={`email-tab-${e.key}`}>{e.label}{!BUILT_IN_KEYS.includes(e.key) && " ★"}</button>
        ))}
      </div>

      {!emailStatus.configured && (
        <div className="pgm-callout" style={{ marginBottom: 14 }}>
          <strong>Resend not configured.</strong> Add your <code>RESEND_API_KEY</code> in <code>backend/.env</code> to enable one-click sending. Until then, use &ldquo;Copy to clipboard&rdquo; to paste into your email client.
        </div>
      )}

      {guest?.email_bounced && (
        <div className="pgm-callout" style={{ marginBottom: 14, borderLeftColor: "#B3261E" }} data-testid="email-bounce-warning">
          <MailX size={14} style={{ verticalAlign: "middle", marginRight: 6, color: "#B3261E" }} />
          <strong>
            {guest.email_bounce_kind === "complained"
              ? `Your "${guest.email_bounce_template || "email"}" was marked as spam — automated emails are paused for this guest.`
              : `Your "${guest.email_bounce_template || "email"}" never reached ${guest.email || "this guest"} — automated emails are paused.`}
          </strong>{" "}
          {guest.email_bounce_reason || "The mail server rejected it."} This guest hasn't ignored you — they never got the email.
          Fix the address on the <strong>Info</strong> tab and automation resumes straight away, with the bounced email queued to go out again.
        </div>
      )}

      {needsBroadcastLink && (
        <div className="pgm-callout" style={{ marginBottom: 14, borderLeftColor: "var(--pgm-orange)" }} data-testid="broadcast-link-warning">
          <AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6, color: "var(--pgm-orange)" }} />
          <strong>Pre-show email is blocked — no studio link on this guest.</strong> Add a StreamYard / Zoom / Restream / Riverside link on the <strong>Info</strong> tab. If you have a default set on Show Settings, tap <strong>Use show default</strong> to copy it in one click.
        </div>
      )}

      <div className="pgm-card" data-testid="email-preview">
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <div>
            <div className="pgm-micro">◆ To</div>
            <div style={{ fontSize: 14 }}>{data.to || <em style={{ opacity: 0.6 }}>(no guest email)</em>}</div>
          </div>
          {lastSentFor(active) && (
            <span className="pgm-badge pgm-badge-olive" data-testid="email-last-sent">
              <CheckCircle2 size={11} /> Last sent {fmtWhen(lastSentFor(active).created_at)}
            </span>
          )}
        </div>
        <div className="pgm-micro" style={{ marginTop: 6 }}>◆ Subject</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--pgm-wine)", marginBottom: 12 }} data-testid="email-subject">{data.subject}</div>
        <div className="pgm-micro">◆ Body</div>
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13.5, lineHeight: 1.55, color: "var(--pgm-ink)", margin: "8px 0 0" }} data-testid="email-body">{data.body}</pre>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button className="pgm-btn pgm-btn-ghost" onClick={copy} data-testid="email-copy-btn"><Copy size={14} /> Copy</button>
        <button
          className="pgm-btn"
          onClick={send}
          disabled={sending || !emailStatus.configured || needsBroadcastLink || !data.to}
          title={!data.to
            ? "Add a guest email address on the Info tab before sending"
            : needsBroadcastLink
              ? "Add a broadcast link to this guest before sending the pre-show email"
              : ""}
          data-testid="email-send-btn"
        >
          <Send size={14} /> {sending
            ? "Sending..."
            : !data.to
              ? "Send blocked — no guest email"
              : needsBroadcastLink
                ? "Send blocked — no studio link"
                : emailStatus.configured ? "Send via Resend" : "Send (configure Resend)"}
        </button>
        {status && (
          <div className={`pgm-badge ${status.kind === "ok" ? "pgm-badge-olive" : "pgm-badge-orange"}`} data-testid="email-status">
            {status.kind === "ok" ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />} {status.text}
          </div>
        )}
      </div>

      <div style={{ marginTop: 28 }} data-testid="email-history">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <History size={15} color="var(--pgm-magenta)" />
          <h3 className="pgm-display" style={{ fontSize: 20, margin: 0 }}>Send history</h3>
        </div>
        {log.length === 0 ? (
          <div className="pgm-callout" data-testid="email-history-empty">
            <strong>No emails sent to this guest yet.</strong> Sends via Resend will be logged here automatically.
          </div>
        ) : (
          <table className="pgm-table" data-testid="email-history-table">
            <thead><tr><th>Email</th><th>To</th><th>Sent by</th><th>When</th><th>Status</th><th>Delivery</th></tr></thead>
            <tbody>
              {log.map(l => (
                <tr key={l.id} data-testid={`email-log-${l.id}`}>
                  <td><strong>{l.template_label || l.template_key || "—"}</strong><br/><span style={{ fontSize: 11, opacity: 0.65 }}>{l.subject}</span></td>
                  <td style={{ fontSize: 12 }}>{l.to}</td>
                  <td style={{ fontSize: 12 }}>{l.sent_by || "—"}</td>
                  <td style={{ fontSize: 12 }}>{fmtWhen(l.created_at)}</td>
                  <td>
                    {l.sent
                      ? <span className="pgm-badge pgm-badge-olive"><CheckCircle2 size={11} /> Accepted</span>
                      : <span className="pgm-badge pgm-badge-orange" title={l.reason || ""}><AlertTriangle size={11} /> Failed</span>}
                  </td>
                  <td><DeliveryChip log={l} fmtWhen={fmtWhen} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={{ fontSize: 11.5, opacity: 0.65, marginTop: 10 }}>
          <strong>Status</strong> is whether Resend accepted the email. <strong>Delivery</strong> is what happened
          next at the guest's mail server — hover any delivery chip for the timeline and the bounce reason.
        </p>
      </div>
    </div>
  );
}

export default EmailsTab;
